import { ChevronRight, Search } from 'lucide-react';
import { Card, Input, Pagination } from '@/design-system';
import type { MigrationLegacyClient } from '../types';

export interface LegacyClientsTableHandlers {
  onSelectClient: (clientId: string) => void;
}

interface LegacyClientsTableProps {
  clients: MigrationLegacyClient[];
  search: string;
  onSearchChange: (value: string) => void;
  activeClientId: string | null;
  loading?: boolean;
  /** Refetch while previous results are still shown (e.g. search/page change). */
  fetching?: boolean;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  handlers: LegacyClientsTableHandlers;
}

export function LegacyClientsTable({
  clients,
  search,
  onSearchChange,
  activeClientId,
  loading = false,
  fetching = false,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  handlers,
}: LegacyClientsTableProps) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-gray-100 p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Clientes do legado</h3>
            <p className="text-sm text-gray-500">
              Clique em um cliente para carregar os telefones.
            </p>
          </div>
          <div className="relative w-full sm:max-w-sm">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar por nome, e-mail ou ID legado..."
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <div className={`divide-y divide-gray-100 ${fetching ? 'opacity-60' : ''}`}>
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse px-4 py-4">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-40 rounded bg-gray-200" />
                  <div className="h-3 w-24 rounded bg-gray-100" />
                </div>
                <div className="mt-1 h-4 w-4 shrink-0 rounded bg-gray-100" />
              </div>
            </div>
          ))
        ) : clients.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Nenhum cliente encontrado com esse filtro.
          </div>
        ) : (
          clients.map((client) => {
            const isActive = client.id === activeClientId;
            return (
              <div
                key={client.id}
                role="button"
                tabIndex={0}
                onClick={() => handlers.onSelectClient(client.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlers.onSelectClient(client.id);
                  }
                }}
                className={`w-full cursor-pointer px-4 py-4 text-left transition-colors hover:bg-gray-50 ${
                  isActive ? 'bg-[#0084FF]/5' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{client.name}</span>
                      {isActive && (
                        <span className="rounded-full bg-[#0084FF]/10 px-2 py-0.5 text-[11px] font-medium text-[#005bb5]">
                          Selecionado
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">ID legado: {client.legacyId}</div>
                  </div>

                  <ChevronRight
                    size={16}
                    className={`mt-1 shrink-0 ${isActive ? 'text-[#0084FF]' : 'text-gray-300'}`}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {!loading && total > 0 ? (
        <Pagination
          page={page}
          totalPages={Math.max(1, totalPages)}
          total={total}
          limit={limit}
          onPageChange={onPageChange}
        />
      ) : null}
    </Card>
  );
}
